import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../../css/BaseSelect/baseSelect.css';
import { ReactComponent as ArrowDown } from '../../../assets/icons/svg/arrowUnder.svg'

// 접근성과 키보드 조작을 고려한 커스텀 드롭다운 컴포넌트
export default function BaseSelect({
  options = [], // [{ label, value, key? }]
  value,
  onChange,
  placeholder = '선택',
  className = '',
  disabled = false,
  ariaLabel,
  variant = 'general', // 'country' | 'region' | 'general'
}) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);
  const displayText = useMemo(() => {
    if (!selected) return placeholder;
    // 전화번호 국가번호의 경우 선택 표시를 다이얼만 노출 (예: +82)
    if (variant === 'phonecode') return selected.value;
    return selected.label;
  }, [selected, placeholder, variant]);
  const hasValue = !!selected;

  const toggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const close = () => setOpen(false);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Set initial highlight when opening
  useEffect(() => {
    if (open) {
      const idx = Math.max(0, options.findIndex((o) => o.value === value));
      setHighlight(idx === -1 ? 0 : idx);
    }
  }, [open, options, value]);

  const commit = (opt) => {
    onChange?.(opt?.value ?? '');
    close();
    // Return focus to button for accessibility
    requestAnimationFrame(() => buttonRef.current?.focus());
  };

  const onKeyDownButton = (e) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        setOpen(true);
        break;
      default:
        break;
    }
  };

  const onKeyDownList = (e) => {
    if (!open) return;
    const last = options.length - 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((h) => (h < last ? h + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((h) => (h > 0 ? h - 1 : last));
        break;
      case 'Home':
        e.preventDefault();
        setHighlight(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlight(last);
        break;
      case 'Enter':
        e.preventDefault();
        if (options[highlight]) commit(options[highlight]);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        buttonRef.current?.focus();
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`custom-dropdown custom-dropdown--${variant} ${hasValue ? 'has-value' : ''} ${open ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}
      data-variant={variant}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`custom-dropdown__toggle ${hasValue ? 'is-selected' : ''}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={toggle}
        onKeyDown={onKeyDownButton}
        disabled={disabled}
      >
        <span className={`custom-dropdown__value ${selected ? '' : 'is-placeholder'}`}>
          {displayText}
        </span>
        <span aria-hidden className="custom-dropdown__chevron">
          <ArrowDown />
        </span>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          className="custom-dropdown__menu"
          onKeyDown={onKeyDownList}
        >
          {options.map((opt, idx) => (
            <li
              key={opt.key || opt.value}
              role="option"
              aria-selected={value === opt.value}
              className={`custom-dropdown__option ${idx === highlight ? 'is-highlighted' : ''} ${value === opt.value ? 'is-selected' : ''}`}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(opt)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
